import React, { useState } from 'react';
import {
  TextField,
  Typography,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { CostItem, CostBreakdown } from '../types/budget';
import { calculateTotalValue } from '../utils/calculations';

interface CostBreakdownFormProps {
  costBreakdown: CostBreakdown;
  onChange: (category: keyof CostBreakdown, items: CostItem[]) => void;
}

const paymentTerms = ['BTB', 'Cash', 'CM', 'Bank/Com', 'Commission'] as const;
const units = ['Kg', 'Pcs', 'Dz', 'Yds', 'GR', 'kgs', 'Pc'];

const CostCategoryForm: React.FC<{
  title: string;
  items: CostItem[];
  category: keyof CostBreakdown;
  onChange: (category: keyof CostBreakdown, items: CostItem[]) => void;
}> = ({ title, items, category, onChange }) => {
  const [newItem, setNewItem] = useState<Partial<CostItem>>({
    description: '',
    paymentTerm: 'BTB',
    unit: 'Kg',
    qty: 0,
    unitPrice: 0,
  });

  const handleAddItem = () => {
    console.log('Adding item:', newItem);
    if (newItem.description && (newItem.unitPrice !== undefined && newItem.unitPrice >= 0)) {
      const item: CostItem = {
        id: Date.now().toString(),
        sl: items.length + 1,
        category: title,
        description: newItem.description || '',
        paymentTerm: newItem.paymentTerm as any,
        unit: newItem.unit || 'Kg',
        qty: newItem.qty || 0,
        unitPrice: newItem.unitPrice || 0,
        totalValue: calculateTotalValue(newItem.qty || 0, newItem.unitPrice || 0),
        supplier: newItem.supplier,
      };
      const updatedItems = [...items, item];
      console.log('Updated items array:', updatedItems);
      onChange(category, updatedItems);
      setNewItem({
        description: '',
        paymentTerm: 'BTB',
        unit: 'Kg',
        qty: 0,
        unitPrice: 0,
      });
    } else {
      console.log('Cannot add item - missing required fields:', {
        description: newItem.description,
        unitPrice: newItem.unitPrice
      });
    }
  };

  const handleDeleteItem = (id: string) => {
    const updatedItems = items.filter(item => item.id !== id).map((item, index) => ({
      ...item,
      sl: index + 1,
    }));
    onChange(category, updatedItems);
  };

  const handleItemChange = (id: string, field: keyof CostItem, value: any) => {
    const updatedItems = items.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'qty' || field === 'unitPrice') {
          updatedItem.totalValue = calculateTotalValue(updatedItem.qty, updatedItem.unitPrice);
        }
        return updatedItem;
      }
      return item;
    });
    onChange(category, updatedItems);
  };

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>SL</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Payment Term</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Unit Price</TableCell>
              <TableCell>Total Value</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.sl}</TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={item.description}
                    onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={item.paymentTerm}
                      onChange={(e) => handleItemChange(item.id, 'paymentTerm', e.target.value)}
                    >
                      {paymentTerms.map((term) => (
                        <MenuItem key={term} value={term}>
                          {term}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={item.unit}
                      onChange={(e) => handleItemChange(item.id, 'unit', e.target.value)}
                    >
                      {units.map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={item.qty}
                    onChange={(e) => handleItemChange(item.id, 'qty', Number(e.target.value))}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) => handleItemChange(item.id, 'unitPrice', Number(e.target.value))}
                    inputProps={{ 
                      step: '0.001',
                      min: '0'
                    }}
                  />
                </TableCell>
                <TableCell>
                  ${item.totalValue.toFixed(2)}
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={item.supplier || ''}
                    onChange={(e) => handleItemChange(item.id, 'supplier', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteItem(item.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {/* New Item Row */}
            <TableRow>
              <TableCell>{items.length + 1}</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="New item description"
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                />
              </TableCell>
              <TableCell>
                <FormControl size="small" fullWidth>
                  <Select
                    value={newItem.paymentTerm}
                    onChange={(e) => setNewItem({ ...newItem, paymentTerm: e.target.value as any })}
                  >
                    {paymentTerms.map((term) => (
                      <MenuItem key={term} value={term}>
                        {term}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <FormControl size="small" fullWidth>
                  <Select
                    value={newItem.unit}
                    onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                  >
                    {units.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="Qty"
                  value={newItem.qty || 0}
                  onChange={(e) => setNewItem({ ...newItem, qty: Number(e.target.value) })}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="Unit Price"
                  value={newItem.unitPrice || 0}
                  onChange={(e) => setNewItem({ ...newItem, unitPrice: Number(e.target.value) })}
                  inputProps={{ 
                    step: '0.001',
                    min: '0'
                  }}
                />
              </TableCell>
              <TableCell>
                ${newItem.qty && newItem.unitPrice ? (newItem.qty * newItem.unitPrice).toFixed(2) : '0.00'}
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Supplier"
                  value={newItem.supplier || ''}
                  onChange={(e) => setNewItem({ ...newItem, supplier: e.target.value })}
                />
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleAddItem}
                  disabled={!newItem.description || (newItem.unitPrice === undefined || newItem.unitPrice < 0)}
                >
                  <AddIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export const CostBreakdownForm: React.FC<CostBreakdownFormProps> = ({ costBreakdown, onChange }) => {
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Cost Breakdown
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      <CostCategoryForm
        title="Yarn"
        items={costBreakdown.yarn}
        category="yarn"
        onChange={onChange}
      />
      
      <CostCategoryForm
        title="Knitting"
        items={costBreakdown.knitting}
        category="knitting"
        onChange={onChange}
      />
      
      <CostCategoryForm
        title="Dyeing"
        items={costBreakdown.dyeing}
        category="dyeing"
        onChange={onChange}
      />
      
      <CostCategoryForm
        title="Printing"
        items={costBreakdown.printing}
        category="printing"
        onChange={onChange}
      />
      
      <CostCategoryForm
        title="AOP"
        items={costBreakdown.aop}
        category="aop"
        onChange={onChange}
      />
      
      <CostCategoryForm
        title="Trims & Accessories"
        items={costBreakdown.trimsAccessories}
        category="trimsAccessories"
        onChange={onChange}
      />
      
      <CostCategoryForm
        title="CM"
        items={costBreakdown.cm}
        category="cm"
        onChange={onChange}
      />
      
      <CostCategoryForm
        title="Commercial"
        items={costBreakdown.commercial}
        category="commercial"
        onChange={onChange}
      />
    </Paper>
  );
};
